import { ref, computed, onUnmounted, type Ref } from 'vue'
import { useAppStore } from '@/stores/app'

export type HomeTab = 'recommend' | 'creation'

const TAB_SNAP_MS = 260 // Tab 切换动画时长，单位毫秒。需要与 CSS 中的 transition-duration 保持一致
const HORIZONTAL_LOCK_THRESHOLD = 18 // 水平锁定阈值，单位像素。水平位移超过该值且明显大于垂直位移时，锁定为水平手势
const VERTICAL_LOCK_THRESHOLD = 10 // 垂直锁定阈值，单位像素。垂直位移超过该值且明显大于水平位移时，锁定为垂直手势
const TAB_SWITCH_THRESHOLD_PX = 64 // Tab 切换阈值，单位像素。水平位移超过该值或快速滑动时触发 Tab 切换
const TAB_SWITCH_THRESHOLD_RATIO = 0.18 // Tab 切换阈值，单位屏宽比例。水平位移超过该比例或快速滑动时触发 Tab 切换
const TAB_SWITCH_VELOCITY = 0.35 // Tab 切换阈值，单位像素/毫秒。滑动速度超过该值时触发 Tab 切换
const EDGE_RESISTANCE = 0.35 // 边缘阻尼系数。当在第一个或最后一个 Tab 继续滑动时，应用该系数进行位移阻尼

export function useHomeGesture(options: {
  tabOrder: HomeTab[]
  viewportRef: Ref<HTMLElement | null>
  onTabChange: (tab: HomeTab) => void
}) {
  const appStore = useAppStore()
  const { tabOrder, viewportRef, onTabChange } = options

  const activeTab = ref<HomeTab>('creation')
  const viewportWidth = ref(375)
  const horizontalOffset = ref(0)
  const isTabSnapping = ref(false)
  const gestureLock = ref<'none' | 'horizontal' | 'vertical'>('none')

  const activeTabIndex = computed(() => tabOrder.indexOf(activeTab.value))
  const sceneWidth = computed(() => `${100 / tabOrder.length}%`)

  const sceneInteractionLocked = computed(() => {
    return gestureLock.value === 'horizontal' || isTabSnapping.value
  })

  const recommendPlaybackEnabled = computed(() => {
    return activeTab.value === 'recommend' && !sceneInteractionLocked.value
  })

  const creationPlaybackEnabled = computed(() => {
    return activeTab.value === 'creation' && !sceneInteractionLocked.value
  })

  const recommendNavigationEnabled = computed(() => {
    return activeTab.value === 'recommend' && gestureLock.value !== 'horizontal' && !isTabSnapping.value
  })

  const creationNavigationEnabled = computed(() => {
    return activeTab.value === 'creation' && gestureLock.value !== 'horizontal' && !isTabSnapping.value
  })

  const carouselTrackStyle = computed(() => {
    const baseOffset = -activeTabIndex.value * viewportWidth.value
    const translateX = baseOffset + horizontalOffset.value
    return {
      width: `${tabOrder.length * 100}%`,
      transform: `translate3d(${translateX}px, 0, 0)`,
      transition: isTabSnapping.value ? `transform ${TAB_SNAP_MS}ms ease` : 'none',
    }
  })

  // Touch state
  let touchStartX = 0
  let touchStartY = 0
  let touchStartTime = 0
  let hasTouchSession = false
  let tabSnapTimer: number | null = null

  const clearTabSnapTimer = () => {
    if (tabSnapTimer !== null) {
      window.clearTimeout(tabSnapTimer)
      tabSnapTimer = null
    }
  }

  const scheduleTabSettle = () => {
    clearTabSnapTimer()
    tabSnapTimer = window.setTimeout(() => {
      isTabSnapping.value = false
      horizontalOffset.value = 0
      gestureLock.value = 'none'
    }, TAB_SNAP_MS)
  }

  const updateViewportWidth = () => {
    const el = viewportRef.value
    // PC 布局下 clientWidth 是去掉侧边栏后的实际内容宽度
    // 仅当 ref 未绑定（el 为 null）时才回退到 window.innerWidth
    const nextWidth = el ? el.clientWidth : window.innerWidth
    viewportWidth.value = Math.max(1, nextWidth)
  }

  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

  const applyEdgeResistance = (rawDeltaX: number) => {
    const isAtFirstTab = activeTabIndex.value === 0 && rawDeltaX > 0
    const isAtLastTab = activeTabIndex.value === tabOrder.length - 1 && rawDeltaX < 0
    if (isAtFirstTab || isAtLastTab) {
      return rawDeltaX * EDGE_RESISTANCE
    }
    return rawDeltaX
  }

  const animateToTab = (nextTab: HomeTab) => {
    if (nextTab === activeTab.value && horizontalOffset.value === 0 && !isTabSnapping.value) {
      gestureLock.value = 'none'
      return
    }

    activeTab.value = nextTab
    horizontalOffset.value = 0
    isTabSnapping.value = true
    scheduleTabSettle()

    onTabChange(nextTab)
  }

  const onTouchStartCapture = (event: TouchEvent) => {
    if (appStore.activeMobilePanel !== 'none' || isTabSnapping.value) return
    if (!event.touches || event.touches.length !== 1) return

    updateViewportWidth()
    touchStartX = event.touches[0]!.clientX
    touchStartY = event.touches[0]!.clientY
    touchStartTime = Date.now()
    hasTouchSession = true
    gestureLock.value = 'none'
  }

  const onTouchMoveCapture = (event: TouchEvent) => {
    if (!hasTouchSession || appStore.activeMobilePanel !== 'none' || !event.touches?.length) return

    const currentTouch = event.touches[0]!
    const deltaX = currentTouch.clientX - touchStartX
    const deltaY = currentTouch.clientY - touchStartY
    const absDeltaX = Math.abs(deltaX)
    const absDeltaY = Math.abs(deltaY)

    if (gestureLock.value === 'none') {
      if (absDeltaX >= HORIZONTAL_LOCK_THRESHOLD && absDeltaX > absDeltaY * 1.3) {
        gestureLock.value = 'horizontal'
      } else if (absDeltaY >= VERTICAL_LOCK_THRESHOLD && absDeltaY > absDeltaX * 1.2) {
        gestureLock.value = 'vertical'
      }
    }

    if (gestureLock.value !== 'horizontal') return

    event.preventDefault()
    event.stopPropagation()
    horizontalOffset.value = applyEdgeResistance(deltaX)
  }

  const onTouchEndCapture = (event: TouchEvent) => {
    if (!hasTouchSession) return

    hasTouchSession = false

    if (gestureLock.value !== 'horizontal') {
      gestureLock.value = 'none'
      return
    }

    event.preventDefault()
    event.stopPropagation()

    const distance = Math.abs(horizontalOffset.value)
    const duration = Math.max(Date.now() - touchStartTime, 1)
    const velocity = distance / duration
    const switchThreshold = Math.max(TAB_SWITCH_THRESHOLD_PX, viewportWidth.value * TAB_SWITCH_THRESHOLD_RATIO)
    const shouldSwitch = distance >= switchThreshold || velocity >= TAB_SWITCH_VELOCITY

    const direction = horizontalOffset.value < 0 ? 1 : -1
    const nextIndex = shouldSwitch
      ? clamp(activeTabIndex.value + direction, 0, tabOrder.length - 1)
      : activeTabIndex.value

    animateToTab(tabOrder[nextIndex]!)
  }

  const onTouchCancelCapture = (event: TouchEvent) => {
    if (!hasTouchSession) return

    hasTouchSession = false

    if (gestureLock.value === 'horizontal') {
      event.preventDefault()
      event.stopPropagation()
    }

    animateToTab(activeTab.value)
  }

  const switchTab = (nextTab: HomeTab) => {
    if (isTabSnapping.value) return
    animateToTab(nextTab)
  }

  onUnmounted(() => {
    clearTabSnapTimer()
    window.removeEventListener('resize', updateViewportWidth)
  })

  return {
    activeTab,
    viewportWidth,
    horizontalOffset,
    isTabSnapping,
    gestureLock,
    activeTabIndex,
    sceneWidth,
    sceneInteractionLocked,
    recommendPlaybackEnabled,
    creationPlaybackEnabled,
    recommendNavigationEnabled,
    creationNavigationEnabled,
    carouselTrackStyle,
    updateViewportWidth,
    onTouchStartCapture,
    onTouchMoveCapture,
    onTouchEndCapture,
    onTouchCancelCapture,
    switchTab,
  }
}
